import { toast } from 'sonner';

/**
 * Vigila si se publicó un build nuevo mientras la pestaña seguía abierta.
 *
 * El index.html se sirve con `Cache-Control: no-store`, pero ese header solo
 * aplica cuando el navegador hace una navegación completa. Un usuario que deja
 * Mexlog abierto durante horas nunca vuelve a pedirlo y se queda con el bundle
 * viejo aunque ya se haya publicado el arreglo. Aquí comparamos el hash del
 * bundle que cargó la pestaña contra el que anuncia el servidor y ofrecemos
 * recargar cuando son distintos.
 */

/** Archivo que CRA publica en cada build con los nombres hasheados de los bundles. */
const MANIFEST_URL = '/asset-manifest.json';

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;

let started = false;
let loadedHash: string | null = null;
let notified = false;

/**
 * Lee del servidor el hash del bundle principal publicado actualmente.
 *
 * @returns {Promise<string|null>} Ruta hasheada de `main.js`, o null si el
 * manifest no está disponible (por ejemplo en `npm start`).
 */
async function fetchMainHash(): Promise<string | null> {
  const response = await fetch(MANIFEST_URL, { cache: 'no-store' });
  if (!response.ok) return null;

  // Cuando el manifest no existe, nginx responde el index.html del SPA con
  // status 200. Verificamos el tipo antes de intentar parsear como JSON.
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return null;

  const manifest = await response.json();
  return manifest?.files?.['main.js'] ?? null;
}

/** Muestra el aviso de versión nueva una sola vez por sesión de pestaña. */
function notifyUpdateAvailable(): void {
  if (notified) return;
  notified = true;

  toast('Hay una versión nueva de Kappa+', {
    description: 'Recarga la página para obtener los últimos cambios.',
    duration: Infinity,
    className: 'toast-halo',
    action: {
      label: 'Actualizar',
      onClick: () => window.location.reload(),
    },
  });
}

/** Compara el build del servidor contra el que cargó esta pestaña. */
async function check(): Promise<void> {
  let hash: string | null = null;

  try {
    hash = await fetchMainHash();
  } catch {
    // Sin red o servidor caído: no molestamos al usuario, se reintenta al
    // siguiente ciclo.
    return;
  }

  if (!hash) return;

  // La primera lectura solo establece la referencia contra la que comparar.
  if (loadedHash === null) {
    loadedHash = hash;
    return;
  }

  if (hash !== loadedHash) notifyUpdateAvailable();
}

/**
 * Arranca la vigilancia de versiones. Es idempotente: llamarla varias veces no
 * duplica los temporizadores.
 *
 * @param {number} intervalMs Cada cuánto revisar el servidor. Default 5 minutos.
 * @returns {void}
 */
export default function startAppUpdateWatcher(intervalMs: number = DEFAULT_INTERVAL_MS): void {
  if (started) return;
  started = true;

  check();
  window.setInterval(check, intervalMs);
  // Revisar al volver a la pestaña cubre la laptop que estuvo suspendida.
  window.addEventListener('focus', check);
}
